/**
 * @fileoverview Development Report page (AR-545) — class-wide curriculum coverage.
 * Shows how many standards/skills were assessed and a per-framework hierarchy of
 * the assessed items, filtered by class and by Standards/Skills.
 */

import { useEffect, useMemo } from 'react'
import { Stack } from '@mantine/core'
import { IconX } from '@tabler/icons-react'
import { useTranslation } from 'react-i18next'
import { Layout } from 'components/Layout'
import { CurrentUserContextProvider } from 'context/useCurrentUser'
import { notifications, NotificationProvider } from 'context'
import {
  DevelopmentReportEmptyState,
  DevelopmentReportFilters,
  DevelopmentReportHeader,
  DevelopmentReportLoading,
  DevelopmentReportStats,
  DevelopmentReportTree,
} from './components'
import { useDevelopmentReport } from './hooks/useDevelopmentReport'

function DevelopmentReportView() {
  const { t } = useTranslation()
  const { classes, report, loading, error, type, classId, setType, setClassId } = useDevelopmentReport()

  useEffect(() => {
    if (!error) return
    notifications.show({
      message: t('development_report.error.load_failed'),
      color: 'red',
      icon: <IconX size={18} />,
    })
  }, [error, t])

  const activeTree = useMemo(() => {
    if (!report) return []
    return type === 'standards' ? report.standards : report.skills
  }, [report, type])

  const renderResults = () => {
    if (loading) return <DevelopmentReportLoading />
    if (!classId) return <DevelopmentReportEmptyState variant='select_class' />
    if (activeTree.length === 0) return <DevelopmentReportEmptyState variant='no_data' />
    return <DevelopmentReportTree items={activeTree} />
  }

  return (
    <Layout>
      <Stack spacing='lg'>
        <DevelopmentReportHeader />

        <DevelopmentReportStats
          standardsAssessed={report?.standardsAssessed ?? 0}
          skillsAssessed={report?.skillsAssessed ?? 0}
        />

        <DevelopmentReportFilters
          classes={classes}
          type={type}
          classId={classId}
          onTypeChange={setType}
          onClassChange={setClassId}
          disabled={loading}
        />

        {renderResults()}
      </Stack>
    </Layout>
  )
}

export function DevelopmentReportPage() {
  return (
    <CurrentUserContextProvider>
      <NotificationProvider>
        <DevelopmentReportView />
      </NotificationProvider>
    </CurrentUserContextProvider>
  )
}
